import {
  createTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from "./table-core.js";

const el = (tag, text, className) => {
  const node = document.createElement(tag);
  if (text !== undefined) node.textContent = text;
  if (className) node.className = className;
  return node;
};
/**
 * @param {{
 *   host: HTMLElement, label: string, columns: any[], detail?: (row: any) => Node,
 *   onRender?: () => void, empty?: string | Node,
 *   manualSorting?: boolean, onSortingChange?: (sorting: { id: string, desc: boolean }[]) => void,
 *   filter?: boolean, pageSize?: number,
 *   groupBy?: ((row: any) => { key: string, label: string, rank?: number }) | null,
 * }} options
 * `manualSorting` (DS-15/API-03): rows arrive already sorted by the server; a
 * header click reports the new sorting through `onSortingChange` instead of
 * reordering locally. `groupBy` renders a header row before each group.
 */
export function createDataTable({
  host,
  label,
  columns,
  detail,
  onRender = () => {},
  empty = "No records yet.",
  manualSorting = false,
  onSortingChange = () => {},
  filter = true,
  pageSize = 15,
  groupBy = null,
}) {
  let data = [],
    loaded = false,
    expanded = new Set(),
    grouping = groupBy;
  const toolbar = el("div", undefined, "ui-table-toolbar"),
    search = el("input", undefined, "ui-input"),
    count = el("span", "", "muted");
  search.type = "search";
  search.placeholder = `Filter ${label.toLowerCase()}…`;
  search.setAttribute("aria-label", `Filter ${label}`);
  if (filter) toolbar.append(search);
  toolbar.append(count);
  const scroll = el("div", undefined, "ui-table-scroll"),
    tableElement = el("table", undefined, "ui-table"),
    head = el("thead"),
    body = el("tbody");
  tableElement.setAttribute("aria-label", label);
  tableElement.append(head, body);
  scroll.append(tableElement);
  const footer = el("div", undefined, "ui-table-footer");
  const control = (text, action) => {
    const button = el("button", text, "ui-control");
    button.type = "button";
    button.addEventListener("click", action);
    return button;
  };
  const previous = control("Previous", () => table.previousPage()),
    next = control("Next", () => table.nextPage()),
    page = el("span", "", "muted");
  footer.append(page, previous, next);
  host.replaceChildren(toolbar, scroll, footer);
  let state = {
    sorting: [],
    globalFilter: "",
    columnFilters: [],
    pagination: { pageIndex: 0, pageSize },
    columnPinning: { left: [], right: [] },
  };
  const defs = columns.map((c) => ({
    id: c.id,
    header: c.label,
    accessorFn: c.value,
    enableSorting: c.sort !== false,
    enableGlobalFilter: c.filter !== false,
  }));
  const table = createTable({
    data,
    columns: defs,
    state,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: "includesString",
    autoResetPageIndex: false,
    manualSorting,
    onStateChange: (updater) => {
      const sorting = state.sorting;
      state = typeof updater === "function" ? updater(state) : updater;
      table.setOptions((old) => ({ ...old, state }));
      render();
      if (manualSorting && state.sorting !== sorting)
        onSortingChange(state.sorting);
    },
  });
  function render() {
    const headers = el("tr");
    for (const column of table.getAllLeafColumns()) {
      const cell = el("th");
      cell.setAttribute("scope", "col");
      if (column.getCanSort()) {
        const sorted = column.getIsSorted();
        cell.setAttribute(
          "aria-sort",
          sorted === "asc"
            ? "ascending"
            : sorted === "desc"
              ? "descending"
              : "none",
        );
        const sort = control(
          `${column.columnDef.header}${sorted === "asc" ? " ↑" : sorted === "desc" ? " ↓" : ""}`,
          () => {
            column.toggleSorting();
            head.querySelector(`[data-sort="${column.id}"]`)?.focus();
          },
        );
        sort.dataset.sort = column.id;
        cell.append(sort);
      } else cell.textContent = column.columnDef.header;
      headers.append(cell);
    }
    if (detail) headers.append(el("th", "Details", "ui-table-detail-head"));
    head.replaceChildren(headers);
    body.replaceChildren();
    const rows = table.getRowModel().rows;
    if (grouping) {
      // Groups keep the order rows arrived in, unless the caller ranks them.
      const groups = new Map();
      for (const row of rows) {
        const group = grouping(row.original);
        if (!groups.has(group.key))
          groups.set(group.key, { ...group, rows: [] });
        groups.get(group.key).rows.push(row);
      }
      const ordered = [...groups.values()].sort(
        (a, b) => (a.rank ?? 0) - (b.rank ?? 0),
      );
      for (const group of ordered) {
        const tr = el("tr", undefined, "ui-table-group"),
          th = el("th");
        th.setAttribute("scope", "colgroup");
        th.colSpan = columns.length + (detail ? 1 : 0);
        th.append(
          el("span", group.label),
          el("span", String(group.rows.length), "ui-table-group-count"),
        );
        tr.dataset.group = group.key;
        tr.append(th);
        body.append(tr);
        for (const row of group.rows) renderRow(row);
      }
    } else for (const row of rows) renderRow(row);
    // DS-15: skeleton rows until the first data arrives, then the real empty state.
    if (!loaded) {
      body.replaceChildren(
        ...[0, 1, 2].map(() => {
          const tr = el("tr", undefined, "ui-table-skeleton");
          tr.setAttribute("aria-hidden", "true");
          for (
            let index = 0;
            index < columns.length + (detail ? 1 : 0);
            index++
          )
            tr.append(el("td", "", ""));
          return tr;
        }),
      );
      count.textContent = "Loading…";
      footer.hidden = true;
      onRender();
      return;
    }
    if (!body.children.length) {
      // ADM-03: `empty` may be an emptyState() node inviting the first record.
      const tr = el("tr"),
        td = el("td", undefined, "ui-table-empty");
      if (data.length) td.textContent = "No matching records.";
      else if (typeof empty === "string") td.textContent = empty;
      else td.append(empty);
      td.colSpan = columns.length + (detail ? 1 : 0);
      tr.append(td);
      body.append(tr);
    }
    const total = table.getFilteredRowModel().rows.length;
    count.textContent = `${total} ${total === 1 ? "record" : "records"}`;
    page.textContent = `Page ${state.pagination.pageIndex + 1} of ${Math.max(1, table.getPageCount())}`;
    previous.disabled = !table.getCanPreviousPage();
    next.disabled = !table.getCanNextPage();
    footer.hidden = total <= state.pagination.pageSize;
    onRender();
  }
  function renderRow(row) {
    const tr = el("tr");
    columns.forEach((column, index) => {
      const td = el("td");
      const value = row.getValue(column.id);
      if (column.render) td.append(column.render(row.original));
      else td.textContent = value == null ? "—" : String(value);
      td.dataset.column = columns[index].label;
      tr.append(td);
    });
    if (detail) {
      const td = el("td"),
        button = control(expanded.has(row.id) ? "Hide" : "View", () => {
          if (expanded.has(row.id)) expanded.delete(row.id);
          else expanded.add(row.id);
          render();
          body.querySelector(`[data-row="${row.id}"]`)?.focus();
        });
      button.dataset.row = row.id;
      button.setAttribute("aria-expanded", String(expanded.has(row.id)));
      button.setAttribute(
        "aria-label",
        `${expanded.has(row.id) ? "Hide" : "View"} ${row.getValue(columns[0].id)}`,
      );
      td.append(button);
      tr.append(td);
    }
    body.append(tr);
    if (detail && expanded.has(row.id)) {
      const dr = el("tr", undefined, "ui-table-detail"),
        cell = el("td");
      cell.colSpan = columns.length + 1;
      cell.append(detail(row.original));
      dr.append(cell);
      body.append(dr);
    }
  }
  search.addEventListener("input", () => {
    state = { ...state, pagination: { ...state.pagination, pageIndex: 0 } };
    table.setGlobalFilter(search.value);
  });
  return {
    setData(rows) {
      data = rows;
      loaded = true;
      expanded = new Set(
        [...expanded].filter((id) => rows.some((row) => row.id === id)),
      );
      table.setOptions((old) => ({ ...old, data, state }));
      const filteredCount = table.getFilteredRowModel().rows.length;
      state = {
        ...state,
        pagination: {
          ...state.pagination,
          pageIndex: Math.min(
            state.pagination.pageIndex,
            Math.max(
              0,
              Math.ceil(filteredCount / state.pagination.pageSize) - 1,
            ),
          ),
        },
      };
      table.setOptions((old) => ({ ...old, data, state }));
      render();
    },
    reveal(id) {
      if (!data.some((row) => row.id === id)) return false;
      search.value = "";
      table.setGlobalFilter("");
      const index = table
        .getSortedRowModel()
        .rows.findIndex((row) => row.id === id);
      if (index < 0) return false;
      expanded.add(id);
      table.setPageIndex(Math.floor(index / state.pagination.pageSize));
      render();
      const control = [...body.querySelectorAll("[data-row]")].find(
        (button) => button.dataset.row === id,
      );
      control?.scrollIntoView?.({ block: "center" });
      control?.focus();
      return true;
    },
    reset() {
      expanded.clear();
      search.value = "";
      state = {
        ...state,
        globalFilter: "",
        sorting: [],
        pagination: { ...state.pagination, pageIndex: 0 },
      };
      this.setData([]);
    },
    /** Show `sorting` without reporting it (a restored server sort). */
    setSorting(sorting) {
      state = { ...state, sorting };
      table.setOptions((old) => ({ ...old, state }));
      render();
    },
    /** Change or clear the grouping of rows. */
    setGroupBy(next) {
      grouping = next;
      render();
    },
    get expanded() {
      return expanded.size > 0;
    },
    get table() {
      return table;
    },
  };
}
